/**
 * StageRenderer Component  
 * Renders individual stages of the Brain's execution
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import GraphVisualization from './GraphVisualization';
import { aslToReactFlow, isASL } from '@/utils/aslToReactFlow';

// Typewriter effect hook
const useTypewriter = (text, speed = 15) => {
  const [displayText, setDisplayText] = useState('');
  
  useEffect(() => {
    if (!text) {
      setDisplayText('');
      return;
    }
    
    let index = 0;
    setDisplayText('');
    
    const timer = setInterval(() => {
      if (index < text.length) {
        setDisplayText(text.slice(0, index + 1));
        index++;
      } else {
        clearInterval(timer);
      }
    }, speed);
    
    return () => clearInterval(timer);
  }, [text, speed]);
  
  return displayText;
};

// Parse content to check for graphData
// Returns null if no valid content (including empty {} payloads)
const parseContent = (contentArray) => {
  if (!contentArray || contentArray.length === 0) return null;
  
  // Check each content item
  for (const item of contentArray) {
    try {
      const parsed = typeof item.content === 'string' ? JSON.parse(item.content) : item.content;
      
      // Check for graphData (pre-computed React Flow format)
      if (parsed && parsed.graphData) {
        return { type: 'graph', data: parsed.graphData };
      }
      
      // Architect wrapper: { asl: { Comment, StartAt, States } } - show Comment as typewriter, then diagram
      // Light Brain / api-deployer often sends ``asl`` as an *escaped JSON string*; normalize to object
      // so ``isASL`` matches (same as Execution History / aslToReactFlow).
      let aslDef = parsed?.asl ?? parsed;
      if (typeof aslDef === 'string') {
        try {
          aslDef = JSON.parse(aslDef);
        } catch {
          aslDef = null;
        }
      }
      if (parsed && aslDef && isASL(aslDef)) {
        const graphData = aslToReactFlow(aslDef);
        if (graphData.nodes.length > 0) {
          const comment = aslDef?.Comment ?? aslDef?.comment ?? '';
          return {
            type: 'asl_preview',
            comment: typeof comment === 'string' ? comment : '',
            data: graphData
          };
        }
      }
      
      // IMPORTANT: Skip empty payloads ({}) - treat as status update only
      if (parsed && typeof parsed === 'object' && Object.keys(parsed).length === 0) {
        continue; // No UI element will render for empty payloads
      }
      
      // Use plain JSON for objects (e.g. non-ASL design in approval flow)
      if (parsed && typeof parsed === 'object') {
        return { type: 'json', data: parsed };
      }
      
      // Return as plain text
      return { type: 'text', data: item.content };
    } catch (e) {
      // If not JSON, return as plain text
      return { type: 'text', data: item.content };
    }
  }
  
  return null;
};

const StageRenderer = ({ stage, isActive, onApprovalDecision, approvalDecisions, onApprove, onDeny, onModify }) => {
  // Check if this stage requires approval
  const requiresApproval = stage.status === 'APPROVAL_REQUIRED' || stage.requiresApproval;
  const taskToken = stage.taskToken || stage.content?.[0]?.taskToken;
  
  // ✅ NEW: Check if user has already made a decision
  const userDecision = approvalDecisions?.[taskToken];
  const hasDecided = !!userDecision;
  
  const isCompleted = stage.status === 'success' || stage.status === 'failed';
  
  // Parse content
  const parsedContent = stage.content ? parseContent(stage.content) : null;
  
  // Typewriter effect: for 'text' or 'asl_preview' comment (only when active)
  const textToDisplay = parsedContent?.type === 'text'
    ? parsedContent.data
    : parsedContent?.type === 'asl_preview'
      ? parsedContent.comment
      : '';
  const displayedText = useTypewriter(textToDisplay, isActive && !isCompleted ? 15 : 0);
  
  // Show status indicator ONLY for currently active stage (but not for graphs/trees/asl_preview)
  const shouldShowStatus = isActive && !isCompleted &&
    parsedContent?.type !== 'graph' && parsedContent?.type !== 'json' && parsedContent?.type !== 'asl_preview';
  
  // Show payload if:
  // 1. Stage has content AND is not active (previous stages with payload)
  // 2. Stage has content AND is completed (final state)
  // 3. IMPORTANT: Stage has graphData, json, or asl_preview (show immediately, even if active)
  const shouldShowPersistedPayload = parsedContent && (
    !isActive || 
    isCompleted || 
    parsedContent.type === 'graph' ||
    parsedContent.type === 'json' ||
    parsedContent.type === 'asl_preview'
  );
  
  return (
    <>
      {/* Status Indicator - ONLY show for currently active stage */}
      <AnimatePresence>
        {shouldShowStatus && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-3 py-3"
          >
            {/* Loading Spinner */}
            <div className="flex-shrink-0">
              <svg
                className="w-5 h-5 text-blue-500 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
            
            {/* Status Text */}
            <span className="text-[0.9375rem] text-gray-600 font-medium">
              {stage.stage_name || stage.description || 'Processing...'}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Persisted Payload - Show for inactive/completed stages OR graphs (graphs show immediately) */}
      {shouldShowPersistedPayload && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          {parsedContent.type === 'asl_preview' ? (
            /* Architect design: Comment (typewriter) + diagram + approval */
            <div className="overflow-hidden">
              {parsedContent.comment && (
                <p className="mb-4 text-[0.9375rem] text-gray-700 leading-relaxed">
                  {isActive && !isCompleted ? displayedText : parsedContent.comment}
                  {isActive && !isCompleted && displayedText.length < textToDisplay.length && (
                    <span className="inline-block w-1 h-4 bg-gray-800 ml-0.5 animate-pulse align-middle" />
                  )}
                </p>
              )}
              <div className="h-96">
                <GraphVisualization data={parsedContent.data} />
              </div>
              {requiresApproval && taskToken && !hasDecided && (
                <div className="mt-4 flex items-center justify-between border border-gray-200 rounded-lg p-3 shadow-none">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span className="text-[0.9375rem] font-semibold text-gray-700">Approval Required</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={onApprove} className="px-2.5 py-1 text-[0.8125rem] font-medium bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-md shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                        Approve
                      </div>
                    </button>
                    <button onClick={onDeny} className="px-2.5 py-1 text-[0.8125rem] font-medium bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-md shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                        Deny
                      </div>
                    </button>
                    <button onClick={onModify} className="px-2.5 py-1 text-[0.8125rem] font-medium bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-md shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        Modify
                      </div>
                    </button>
                  </div>
                </div>
              )}
              {requiresApproval && taskToken && hasDecided && (
                <div className="mt-4 px-4 py-3 bg-gray-100 rounded-lg border-l-4 border-blue-500">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[0.9375rem] font-medium text-gray-700">
                      You selected: <span className="font-semibold text-blue-700">{userDecision.decision}</span>
                    </span>
                  </div>
                </div>
              )}
            </div>
          ) : parsedContent.type === 'graph' ? (
            /* Render Graph Visualization - Before approval and after deployment */
            <div className="overflow-hidden">
              <div className="h-96">
                <GraphVisualization data={parsedContent.data} />
              </div>
              {/* Approval bar when diagram is shown at approval stage */}
              {requiresApproval && taskToken && !hasDecided && (
                <div className="mt-4 flex items-center justify-between border border-gray-200 rounded-lg p-3 shadow-none">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span className="text-[0.9375rem] font-semibold text-gray-700">Approval Required</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={onApprove} className="px-2.5 py-1 text-[0.8125rem] font-medium bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-md shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                        Approve
                      </div>
                    </button>
                    <button onClick={onDeny} className="px-2.5 py-1 text-[0.8125rem] font-medium bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-md shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                        Deny
                      </div>
                    </button>
                    <button onClick={onModify} className="px-2.5 py-1 text-[0.8125rem] font-medium bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-md shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        Modify
                      </div>
                    </button>
                  </div>
                </div>
              )}
              {requiresApproval && taskToken && hasDecided && (
                <div className="mt-4 px-4 py-3 bg-gray-100 rounded-lg border-l-4 border-blue-500">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[0.9375rem] font-medium text-gray-700">
                      You selected: <span className="font-semibold text-blue-700">{userDecision.decision}</span>
                    </span>
                  </div>
                </div>
              )}
            </div>
          ) : parsedContent.type === 'json' ? (
            /* Render JSON - ASL/design for approval (plain text to avoid tree substring errors) */
            <div className="text-gray-800 max-w-3xl overflow-x-auto">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-[0.9375rem]">
                <pre className="font-mono text-[0.8125rem] text-gray-800 whitespace-pre-wrap break-words">
                  {typeof parsedContent.data === 'string'
                    ? parsedContent.data
                    : JSON.stringify(parsedContent.data, null, 2)}
                </pre>
              </div>

              {/* Inline Approval Bar - Show when approval required and user hasn't decided */}
              {requiresApproval && taskToken && !hasDecided && (
                <div className="mt-4 flex items-center justify-between border border-gray-200 rounded-lg p-3 shadow-none">
                  {/* Left Side: Spinner + Status Text */}
                  <div className="flex items-center gap-3">
                    <svg
                      className="w-5 h-5 text-blue-500 animate-spin"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    <span className="text-[0.9375rem] font-semibold text-gray-700">Approval Required</span>
                  </div>

                  {/* Right Side: Action Buttons */}
                  <div className="flex items-center gap-2">
                    {/* Approve Button */}
                    <button
                      onClick={onApprove}
                      className="px-2.5 py-1 text-[0.8125rem] font-medium bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-md shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all"
                    >
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                        Approve
                      </div>
                    </button>

                    {/* Deny Button */}
                    <button
                      onClick={onDeny}
                      className="px-2.5 py-1 text-[0.8125rem] font-medium bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-md shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all"
                    >
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Deny
                      </div>
                    </button>

                    {/* Modify Button */}
                    <button
                      onClick={onModify}
                      className="px-2.5 py-1 text-[0.8125rem] font-medium bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-md shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all"
                    >
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Modify
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* Show decision status if user already decided */}
              {requiresApproval && taskToken && hasDecided && (
                <div className="mt-4 px-4 py-3 bg-gray-100 rounded-lg border-l-4 border-blue-500">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[0.9375rem] font-medium text-gray-700">
                      You selected: <span className="font-semibold text-blue-700">{userDecision.decision}</span>
                    </span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Render Text - Clean, flat design */
            <div className="text-gray-800 max-w-3xl">
              <div className="whitespace-pre-line text-[0.9375rem] leading-relaxed">
                {parsedContent.data}
              </div>

              {/* Inline Approval Bar - Show when approval required and user hasn't decided */}
              {requiresApproval && taskToken && !hasDecided && (
                <div className="mt-4 flex items-center justify-between border border-gray-200 rounded-lg p-3 shadow-none">
                  <div className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span className="text-[0.9375rem] font-semibold text-gray-700">Approval Required</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={onApprove} className="px-2.5 py-1 text-[0.8125rem] font-medium bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-md shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                        Approve
                      </div>
                    </button>
                    <button onClick={onDeny} className="px-2.5 py-1 text-[0.8125rem] font-medium bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-md shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                        Deny
                      </div>
                    </button>
                    <button onClick={onModify} className="px-2.5 py-1 text-[0.8125rem] font-medium bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-md shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        Modify
                      </div>
                    </button>
                  </div>
                </div>
              )}
              {requiresApproval && taskToken && hasDecided && (
                <div className="mt-4 px-4 py-3 bg-gray-100 rounded-lg border-l-4 border-blue-500">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[0.9375rem] font-medium text-gray-700">
                      You selected: <span className="font-semibold text-blue-700">{userDecision.decision}</span>
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </motion.div>
      )}
      
      {/* Typewriter animation - ONLY for active stage with content */}
      {parsedContent && isActive && !isCompleted && parsedContent.type === 'text' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="text-gray-800 max-w-3xl">
            <div className="whitespace-pre-line text-[0.9375rem] leading-relaxed">
              {displayedText}
              {displayedText.length < textToDisplay.length && (
                <span className="inline-block w-1 h-4 bg-gray-800 ml-0.5 animate-pulse" />
              )}
            </div>
            
            {/* Inline Approval Bar - Show when approval required and user hasn't decided (during typewriter) */}
            {requiresApproval && taskToken && !hasDecided && displayedText === textToDisplay && (
              <div className="mt-4 flex items-center justify-between border border-gray-200 rounded-lg p-3 shadow-none">
                {/* Left Side: Spinner + Status Text */}
                <div className="flex items-center gap-3">
                  <svg
                    className="w-5 h-5 text-blue-500 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span className="text-[0.9375rem] font-semibold text-gray-700">Approval Required</span>
                </div>

                {/* Right Side: Action Buttons */}
                <div className="flex items-center gap-2">
                  {/* Approve Button */}
                  <button
                    onClick={onApprove}
                    className="px-2.5 py-1 text-[0.8125rem] font-medium bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-md shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all"
                  >
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                      Approve
                    </div>
                  </button>

                  {/* Deny Button */}
                  <button
                    onClick={onDeny}
                    className="px-2.5 py-1 text-[0.8125rem] font-medium bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-md shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all"
                  >
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Deny
                    </div>
                  </button>

                  {/* Modify Button */}
                  <button
                    onClick={onModify}
                    className="px-2.5 py-1 text-[0.8125rem] font-medium bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-md shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all"
                  >
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Modify
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Show decision status if user already decided (even during typewriter) */}
            {requiresApproval && taskToken && hasDecided && displayedText === textToDisplay && (
              <div className="mt-4 px-4 py-3 bg-gray-100 rounded-lg border-l-4 border-blue-500">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-[0.9375rem] font-medium text-gray-700">
                    You selected: <span className="font-semibold text-blue-700">{userDecision.decision}</span>
                  </span>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
      
      {/* When there's no content (design was hidden to avoid duplication) */}
      {requiresApproval && taskToken && !parsedContent && isActive && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          {/* Inline Approval Bar - Show if user hasn't decided */}
          {!hasDecided && (
            <div className="flex items-center justify-between border border-gray-200 rounded-lg p-3 shadow-none">
              {/* Left Side: Spinner + Status Text */}
              <div className="flex items-center gap-3">
                <svg
                  className="w-5 h-5 text-blue-500 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span className="text-[0.9375rem] font-semibold text-gray-700">Approval Required</span>
              </div>

              {/* Right Side: Action Buttons */}
              <div className="flex items-center gap-2">
                {/* Approve Button */}
                <button
                  onClick={onApprove}
                  className="px-2.5 py-1 text-[0.8125rem] font-medium bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-md shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all"
                >
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                    Approve
                  </div>
                </button>

                {/* Deny Button */}
                <button
                  onClick={onDeny}
                  className="px-2.5 py-1 text-[0.8125rem] font-medium bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-md shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all"
                >
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Deny
                  </div>
                </button>

                {/* Modify Button */}
                <button
                  onClick={onModify}
                  className="px-2.5 py-1 text-[0.8125rem] font-medium bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-md shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all"
                >
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Modify
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Show decision status if user already decided */}
          {hasDecided && (
            <div className="px-4 py-3 bg-gray-100 rounded-lg border-l-4 border-blue-500">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-[0.9375rem] font-medium text-gray-700">
                  You selected: <span className="font-semibold text-blue-700">{userDecision.decision}</span>
                </span>
              </div>
            </div>
          )}
        </motion.div>
      )}
    </>
  );
};

export default StageRenderer;
